/**
 * Probing Question Card (DC-725)
 *
 * Card component for displaying and answering AI-generated probing questions.
 * Shows question, category, importance, and allows user to submit answers.
 */

import React, { useState } from 'react';
import './contextual-validity.css';

// ============================================================================
// TYPES
// ============================================================================

export type ProbingQuestionCategory = 'data_collection' | 'population_coverage' | 'label_quality' | 'historical_bias' | 'concept_drift' | 'other';

export interface ProbingQuestion {
  id: string;
  question: string;
  category: ProbingQuestionCategory | string;
  importance: 'low' | 'medium' | 'high';
  answered?: boolean;
  answer?: string;
}

interface ProbingQuestionCardProps {
  question: ProbingQuestion;
  journeyId: string;
  onAnswer: (questionId: string, answer: string) => void;
  disabled?: boolean;
}

// ============================================================================
// HELPERS
// ============================================================================

/**
 * Get category icon as Unicode character (safe - no XSS risk)
 */
const getCategoryIcon = (category: string): string => {
  switch (category) {
    case 'data_collection': return '\u{1F4E6}'; // Package
    case 'population_coverage': return '\u{1F465}'; // Busts
    case 'label_quality': return '\u{1F3F7}'; // Tag
    case 'historical_bias': return '\u{1F551}'; // Clock
    case 'concept_drift': return '\u{1F4C8}'; // Chart
    default: return '\u2753'; // Question mark
  }
};

const getCategoryLabel = (category: string): string => {
  switch (category) {
    case 'data_collection': return 'Data Collection';
    case 'population_coverage': return 'Population Coverage';
    case 'label_quality': return 'Label Quality';
    case 'historical_bias': return 'Historical Bias';
    case 'concept_drift': return 'Concept Drift';
    default: return 'Other';
  }
};

const getImportanceColor = (importance: string): string => {
  switch (importance) {
    case 'high': return 'importance-high';
    case 'medium': return 'importance-medium';
    case 'low': return 'importance-low';
    default: return '';
  }
};

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export const ProbingQuestionCard: React.FC<ProbingQuestionCardProps> = ({
  question,
  journeyId,
  onAnswer,
  disabled = false,
}) => {
  const [answer, setAnswer] = useState(question.answer || '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isEditing, setIsEditing] = useState(!question.answered);

  const handleSubmit = async () => {
    if (!answer.trim()) return;

    try {
      setSaving(true);
      setError(null);

      const response = await fetch(
        `/api/contextual-validity/journeys/${journeyId}/questions/${question.id}/answer`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ answer: answer.trim() }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to save answer');
      }

      onAnswer(question.id, answer.trim());
      setIsEditing(false);
    } catch (err: unknown) {
      console.error('[ProbingQuestionCard] Submit error:', err);
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = () => {
    setIsEditing(true);
  };

  return (
    <div className={`probing-question-card ${question.answered ? 'answered' : 'unanswered'}`}>
      <div className="question-header">
        <div className="question-meta">
          <span className="category-icon">
            {getCategoryIcon(question.category)}
          </span>
          <span className="category-label">{getCategoryLabel(question.category)}</span>
          <span className={`importance-badge ${getImportanceColor(question.importance)}`}>
            {question.importance.toUpperCase()}
          </span>
        </div>
        {question.answered && (
          <span className="answered-badge">
            &#10003; Answered
          </span>
        )}
      </div>

      <div className="question-text">
        <p>{question.question}</p>
      </div>

      {error && (
        <div className="question-error">
          <span className="error-icon">&#9888;</span>
          {error}
        </div>
      )}

      {isEditing ? (
        <div className="question-answer-form">
          <textarea
            value={answer}
            onChange={(e) => setAnswer(e.target.value)}
            placeholder="Enter your answer..."
            rows={3}
            disabled={saving || disabled}
          />
          <div className="answer-actions">
            {question.answered && (
              <button
                type="button"
                className="btn-secondary"
                onClick={() => {
                  setAnswer(question.answer || '');
                  setIsEditing(false);
                }}
                disabled={saving}
              >
                Cancel
              </button>
            )}
            <button
              type="button"
              className="btn-primary"
              onClick={handleSubmit}
              disabled={saving || !answer.trim() || disabled}
            >
              {saving ? (
                <>
                  <span className="spinner-small"></span>
                  Saving...
                </>
              ) : (
                'Submit Answer'
              )}
            </button>
          </div>
        </div>
      ) : (
        <div className="question-answer-display">
          <div className="saved-answer">
            <strong>Your answer:</strong>
            <p>{question.answer}</p>
          </div>
          <button
            type="button"
            className="btn-link"
            onClick={handleEdit}
            disabled={disabled}
          >
            Edit Answer
          </button>
        </div>
      )}
    </div>
  );
};

export default ProbingQuestionCard;
